// Browser-cached home / sub-menu badge counts, keyed per warehouse.
//
// All badge tiles are now served by a single GET /badges read (filled
// server-side from the badge_count table). Seeding from localStorage means
// the badges show instantly on mount and survive navigation instead of
// flashing empty until the first poll lands — the poll then refreshes the
// cache. Best-effort: storage may be unavailable (incognito / quota).

export type BadgeCounts = {
    putAway: { count: number };
    pickQueue: { count: number };
    shelfDrop: { items: number; totes: number };
    forecastDrop: { items: number; totes: number };
    pickFace: { items: number; totes: number };
    zoneStorage: { items: number; totes: number };
    dispatch: { items: number; count: number };
    flaggedOrders: { count: number };
    // count = units a leader still has to fetch from high racking;
    // putBack = shelved units waiting to go back on the racking.
    flaggedPicks: { count: number; putBack: number };
    palletReceiving: { pendingCount: number; placedCount: number };
    conveyor: { collect: number; addPallet: number };
};

export const EMPTY_BADGE_COUNTS: BadgeCounts = {
    putAway: { count: 0 },
    pickQueue: { count: 0 },
    shelfDrop: { items: 0, totes: 0 },
    forecastDrop: { items: 0, totes: 0 },
    pickFace: { items: 0, totes: 0 },
    zoneStorage: { items: 0, totes: 0 },
    dispatch: { items: 0, count: 0 },
    flaggedOrders: { count: 0 },
    flaggedPicks: { count: 0, putBack: 0 },
    palletReceiving: { pendingCount: 0, placedCount: 0 },
    conveyor: { collect: 0, addPallet: 0 },
};

const keyFor = (warehouse: string) => `badge-counts:${warehouse}`;

export const readCachedBadgeCounts = (warehouse: string): BadgeCounts => {
    if (!warehouse) return EMPTY_BADGE_COUNTS;
    try {
        const raw = window.localStorage.getItem(keyFor(warehouse));
        if (!raw) return EMPTY_BADGE_COUNTS;
        const parsed = JSON.parse(raw);
        // Merge over the empty shape so a stale cache written by an older
        // build (missing a key) can never produce `undefined.items`.
        return { ...EMPTY_BADGE_COUNTS, ...parsed };
    } catch {
        return EMPTY_BADGE_COUNTS;
    }
};

export const writeCachedBadgeCounts = (warehouse: string, value: BadgeCounts): void => {
    if (!warehouse) return;
    try {
        window.localStorage.setItem(keyFor(warehouse), JSON.stringify(value));
    } catch {
        // Storage may be unavailable (incognito / quota) — caching is best-effort.
    }
};
